import React, { useState, useEffect } from 'react';
import { Building2, Users, Bot, Volume2, CreditCard, Shield, Archive, X, HelpCircle } from 'lucide-react';
import { OrgProvider, useOrg } from '../context/OrgContext';
import { openHelpAgent } from '../utils/openHelpAgent';
import { useAuth } from '../context/AuthContext';
import { AccountProfileSection } from './account/AccountProfileSection';
import { AccountMembersSection } from './account/AccountMembersSection';
import { AccountAISection } from './account/AccountAISection';
import { AccountVoicesSection } from './account/AccountVoicesSection';
import { AccountBillingSection } from './account/AccountBillingSection';
import { AccountSecuritySection } from './account/AccountSecuritySection';
import ArchiveManagerSection from './account/ArchiveManagerSection';

export const ACCOUNT_SECTIONS = [
    { id: 'profile', label: 'Organization Profile', icon: Building2 },
    { id: 'members', label: 'Team Members', icon: Users },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'ai', label: 'AI Engines', icon: Bot },
    { id: 'voices', label: 'Voices', icon: Volume2 },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'archive', label: 'Archives', icon: Archive },
];

// Templates now belong to Channels > Bindings. Stale callers asking AccountPanel
// for either former template route land safely on the account profile.
export const resolveAccountSection = (id) => (
    ACCOUNT_SECTIONS.some(section => section.id === id) ? id : 'profile'
);

function AccountPanelInner({ onClose, initialSection = 'profile', onArchiveStateChange, locked = false, onSignOut }) {
    const [activeSection, setActiveSection] = useState(() => resolveAccountSection(initialSection));
    const { user } = useAuth();
    const { orgId, org, loading, error, setError, loadOrg, loadMembers } = useOrg();
    const canManageBilling = user?.role === 'superuser' || user?.org_role === 'owner';
    const visibleSections = locked
        ? ACCOUNT_SECTIONS.filter(section => section.id === 'billing')
        : ACCOUNT_SECTIONS.filter(section => section.id !== 'billing' || canManageBilling);

    useEffect(() => {
        loadOrg();
        loadMembers();
    }, [loadOrg, loadMembers]);

    useEffect(() => {
        if (activeSection === 'billing' && !canManageBilling) {
            setActiveSection('profile');
        }
    }, [activeSection, canManageBilling]);

    const renderSection = () => {
        switch (activeSection) {
            case 'profile': return <AccountProfileSection />;
            case 'members': return <AccountMembersSection />;
            case 'security': return <AccountSecuritySection />;
            case 'ai': return <AccountAISection />;
            case 'voices': return <AccountVoicesSection />;
            case 'billing': return <AccountBillingSection />;
            case 'archive': return <ArchiveManagerSection onArchiveStateChange={onArchiveStateChange} />;
            default: return <AccountProfileSection />;
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.panel}>
                {/* Sidebar */}
                <div style={styles.sidebar}>
                    <div style={styles.sidebarHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h2 style={styles.sidebarTitle}>Account</h2>
                            <button
                                onClick={() => openHelpAgent({
                                    context: 'settings',
                                    userId: user?.user_id,
                                    userName: user?.name || user?.display_name,
                                    orgId: user?.org_id,
                                    // A used-up free account does not get the
                                    // assistant. sam-dbam sends the wording.
                                    onRefused: (message) => window.alert(message),
                                })}
                                title="Open Sam Help Agent"
                                style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4, borderRadius: 4 }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.background = 'transparent'; }}
                            >
                                <HelpCircle size={18} />
                            </button>
                        </div>
                        {org && <p style={styles.orgName}>{org.name || 'Loading…'}</p>}
                    </div>
                    <nav style={styles.nav}>
                        {visibleSections.map(section => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                style={{
                                    ...styles.navItem,
                                    ...(activeSection === section.id ? styles.navItemActive : {}),
                                }}
                                onMouseEnter={e => {
                                    if (activeSection !== section.id) e.target.style.background = 'rgba(255,255,255,0.05)';
                                }}
                                onMouseLeave={e => {
                                    if (activeSection !== section.id) e.target.style.background = 'transparent';
                                }}
                            >
                                <section.icon size={16} style={{ marginRight: 10, flexShrink: 0, opacity: activeSection === section.id ? 1 : 0.6 }} />
                                {section.label}
                            </button>
                        ))}
                    </nav>

                    {locked && onSignOut && (
                        <button onClick={onSignOut} style={styles.signOutBtn}>Sign out</button>
                    )}

                </div>

                {/* Content */}
                <div style={styles.content}>
                    {/* Close X at top-right of content area */}
                    <div style={styles.contentHeader}>
                        <div style={{ flex: 1 }} />
                        {!locked && (
                            <button
                                onClick={onClose}
                                style={styles.closeBtn}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#F1F5F9'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                                title="Close settings"
                                aria-label="Close settings"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                    {error && (
                        <div style={styles.errorBanner}>
                            <span>{error}</span>
                            <button onClick={() => setError('')} style={styles.dismissBtn}><X size={14} /></button>
                        </div>
                    )}
                    {!orgId ? (
                        <div style={styles.loadingContainer}>
                            <p style={{ color: '#94A3B8', fontSize: 16 }}>
                                No organization linked to your account.
                            </p>
                            <p style={{ color: '#64748B', fontSize: 13, marginTop: 8 }}>
                                Contact your administrator to be added to an organization.
                            </p>
                        </div>
                    ) : loading && !org ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner} />
                            <p style={{ color: '#94A3B8', marginTop: 12 }}>Loading account…</p>
                        </div>
                    ) : (
                        renderSection()
                    )}
                </div>
            </div>
        </div>
    );
}

export function AccountPanel({ orgId, onClose, initialSection = 'profile', onArchiveStateChange, locked = false, onSignOut }) {
    console.log('[AccountPanel] rendered with orgId:', orgId);
    return (
        <OrgProvider orgId={orgId}>
            <AccountPanelInner
                onClose={onClose}
                initialSection={initialSection}
                onArchiveStateChange={onArchiveStateChange}
                locked={locked}
                onSignOut={onSignOut}
            />
        </OrgProvider>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 9000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    panel: {
        width: '90vw',
        maxWidth: 1100,
        height: '85vh',
        background: '#0F172A',
        borderRadius: 16,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
        display: 'flex',
        overflow: 'hidden',
    },
    sidebar: {
        width: 260,
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        background: '#0B1222',
    },
    sidebarHeader: {
        padding: '24px 20px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    sidebarTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 700,
        color: '#F1F5F9',
        letterSpacing: '-0.02em',
    },
    orgName: {
        margin: '6px 0 0',
        fontSize: 13,
        color: '#64748B',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    nav: {
        flex: 1,
        padding: '12px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    navItem: {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        border: 'none',
        borderRadius: 8,
        background: 'transparent',
        color: '#94A3B8',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    navItemActive: {
        background: 'rgba(59, 130, 246, 0.12)',
        color: '#60A5FA',
        fontWeight: 600,
    },
    signOutBtn: {
        margin: 12,
        padding: '10px 14px',
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'transparent',
        color: '#CBD5E1',
        cursor: 'pointer',
        fontSize: 14,
    },
    contentHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
    },
    closeBtn: {
        width: 32,
        height: 32,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: '#94A3B8',
        cursor: 'pointer',
        padding: 0,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s ease',
        flexShrink: 0,
    },
    content: {
        flex: 1,
        overflow: 'auto',
        padding: '32px 40px',
    },
    errorBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        background: 'rgba(239, 68, 68, 0.12)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: 8,
        color: '#FCA5A5',
        fontSize: 13,
        marginBottom: 20,
    },
    dismissBtn: {
        background: 'transparent',
        border: 'none',
        color: '#FCA5A5',
        cursor: 'pointer',
        fontSize: 14,
        padding: '2px 6px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    spinner: {
        width: 32,
        height: 32,
        border: '3px solid rgba(255,255,255,0.1)',
        borderTopColor: '#60A5FA',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};
